import { useEffect, useRef, useState } from "react";

import { applyViewerTheme, getViewerTheme, getViewerThemeLabel, VIEWER_THEMES } from "../lib/theme";
import type { ViewerThemeId } from "../lib/theme";
import styles from "./DevMenu.module.scss";

export interface DevMenuProps {
  onReconnect: () => Promise<void> | void;
  onRestoreVideo: () => Promise<void> | void;
  onThemeChange?: (label: string) => void;
}

export function DevMenu({ onReconnect, onRestoreVideo, onThemeChange }: DevMenuProps) {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<ViewerThemeId>(() => getViewerTheme());
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const selectTheme = (id: string) => {
    const applied = applyViewerTheme(id);
    setTheme(applied);
    setOpen(false);
    onThemeChange?.(getViewerThemeLabel(applied));
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={styles.toggle}
        id="dev-menu-toggle"
        aria-label="Dev-меню"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <i className="bi bi-gear" />
      </button>
      <div
        className={`${styles.popover}${open ? ` ${styles.open}` : ""}`}
        id="dev-menu-popover"
        role="menu"
      >
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Session</div>
          <button
            type="button"
            className={styles.item}
            role="menuitem"
            onClick={async () => {
              setOpen(false);
              await onReconnect();
            }}
          >
            Переподключить сессию
          </button>
          <button
            type="button"
            className={styles.item}
            role="menuitem"
            onClick={async () => {
              setOpen(false);
              await onRestoreVideo();
            }}
          >
            Восстановить видео
          </button>
        </div>
        <div
          className={`${styles.section} ${styles.divider}`}
          role="group"
          aria-label="Color scheme"
        >
          <div className={styles.sectionTitle}>Color scheme</div>
          <div className={styles.themeOptions} role="radiogroup" aria-label="Color scheme">
            {Object.keys(VIEWER_THEMES).map((id) => {
              const tid = id as ViewerThemeId;
              return (
                <button
                  key={tid}
                  type="button"
                  className={`${styles.themeOption} ${styles[tid]}${theme === tid ? ` ${styles.active}` : ""}`}
                  role="menuitemradio"
                  aria-checked={theme === tid}
                  title={VIEWER_THEMES[tid].label}
                  aria-label={VIEWER_THEMES[tid].label}
                  onClick={() => selectTheme(tid)}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
